import { useState } from 'react';

interface ImageUploadProps {
  currentImage: string;
  onImageChange: (imagePath: string) => void;
  label: string;
}

export default function ImageUpload({ currentImage, onImageChange, label }: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentImage);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor, selecione apenas imagens');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Imagem muito grande. Máximo 5MB');
      return;
    }

    setIsUploading(true);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('https://mulata.wortik.com/bolacha-mulata/api/upload.php', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        onImageChange(result.path);
        setPreview(result.path);
      } else {
        setError(result.error || 'Erro ao fazer upload');
        setPreview(currentImage);
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor');
      setPreview(currentImage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {/* Current Image Preview */}
      {preview && (
        <div className="relative w-full max-w-xs border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-contain"
            onError={() => setPreview('/Imagens/placeholder.png')}
          />
        </div>
      )}

      {/* Image Path Display (Read-only) */}
      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
        <p className="text-xs text-gray-600 break-all">{currentImage || 'Nenhuma imagem selecionada'}</p>
      </div>

      {/* File Upload Button */}
      <label className="block cursor-pointer">
        <div className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition duration-200 ${
          isUploading 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-blue-600 hover:bg-blue-700'
        } text-white font-medium`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          {isUploading ? 'Enviando...' : 'Escolher e Fazer Upload'}
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileChange}
          disabled={isUploading}
          className="hidden"
        />
      </label>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}

      <p className="text-xs text-gray-500">
        📁 Formatos: JPG, PNG, GIF, WEBP | 📏 Máximo: 5MB
      </p>
    </div>
  );
}
